import {
  apiErrorFromResponse,
  CSRF_HEADER,
  ensureCsrfCookie,
  isApiError,
  readCsrfToken,
} from '@multaqa/api-client'
import { apiOrigin, fetchWithSessionActivity } from './api'
import { type LoginCountry, looksLikeEmail } from './phone-login'

export type LoginResponse = { two_factor: boolean }
export type TwoFactorQrCode = { svg: string; url: string }
export type TwoFactorRecoveryCodes = string[]

function apiBaseUrl(): string {
  return apiOrigin
}

export async function fortifyPost<TResponse>(path: string, body: unknown): Promise<TResponse> {
  const baseUrl = apiBaseUrl()

  await ensureCsrfCookie(baseUrl)
  const token = readCsrfToken()

  const response = await fetchWithSessionActivity(`${baseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await apiErrorFromResponse(response)
  }

  if (response.status === 204) {
    return undefined as TResponse
  }

  return (await response.json()) as TResponse
}

async function fortifyPut<TResponse>(path: string, body: unknown): Promise<TResponse> {
  const baseUrl = apiBaseUrl()

  await ensureCsrfCookie(baseUrl)
  const token = readCsrfToken()

  const response = await fetchWithSessionActivity(`${baseUrl}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await apiErrorFromResponse(response)
  }

  if (response.status === 204) {
    return undefined as TResponse
  }

  return (await response.json()) as TResponse
}

async function fortifyGet<TResponse>(path: string): Promise<TResponse> {
  const baseUrl = apiBaseUrl()

  await ensureCsrfCookie(baseUrl)
  const token = readCsrfToken()

  const response = await fetchWithSessionActivity(`${baseUrl}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
  })

  if (!response.ok) {
    throw await apiErrorFromResponse(response)
  }

  return (await response.json()) as TResponse
}

export async function login(input: {
  login: string
  country: LoginCountry
  password: string
  remember: boolean
}): Promise<LoginResponse> {
  return fortifyPost<LoginResponse>('/login', {
    login: input.login,
    ...(!looksLikeEmail(input.login) ? { country: input.country } : {}),
    password: input.password,
    remember: input.remember,
  })
}

export async function verifyTwoFactor(input: {
  code?: string
  recovery_code?: string
}): Promise<void> {
  await fortifyPost<void>('/two-factor-challenge', input)
}

export async function forgotPassword(input: { email: string }): Promise<void> {
  await fortifyPost<void>('/forgot-password', input)
}

export async function resetPassword(input: {
  token: string
  email: string
  password: string
  password_confirmation: string
}): Promise<void> {
  await fortifyPost<void>('/reset-password', input)
}

export async function updatePassword(input: {
  current_password: string
  password: string
  password_confirmation: string
}): Promise<void> {
  await fortifyPut<void>('/user/password', input)
}

export async function confirmedPasswordStatus(): Promise<{ confirmed: boolean }> {
  return fortifyGet<{ confirmed: boolean }>('/user/confirmed-password-status')
}

export async function confirmPassword(input: { password: string }): Promise<void> {
  await fortifyPost<void>('/user/confirm-password', input)
}

export async function ensurePasswordConfirmed(): Promise<boolean> {
  const status = await confirmedPasswordStatus()
  return status.confirmed
}

export async function enableTwoFactor(): Promise<void> {
  await fortifyPost<void>('/user/two-factor-authentication', {})
}

export async function twoFactorQrCode(): Promise<TwoFactorQrCode> {
  return fortifyGet<TwoFactorQrCode>('/user/two-factor-qr-code')
}

export async function confirmTwoFactorAuthentication(input: { code: string }): Promise<void> {
  await fortifyPost<void>('/user/confirmed-two-factor-authentication', input)
}

export async function twoFactorRecoveryCodes(): Promise<TwoFactorRecoveryCodes> {
  return fortifyGet<TwoFactorRecoveryCodes>('/user/two-factor-recovery-codes')
}

export function firstFieldError(error: unknown, field: string): string | null {
  if (isApiError(error) && error.errors?.[field]?.[0]) {
    return error.errors[field][0] as string
  }
  return null
}
